import { defineComponent as _defineComponent } from 'vue'
import { getAllPriceBookItems } from '@/api/price-book'
import { computed } from 'vue'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import { useStore } from 'vuex-composition-helpers'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import { useHubs } from '@/common/useHubs'
import { orderBy } from "lodash"
import PriceBookItem from "@/models/price-book-item"

interface Props {
  value: string
  assignedToServiceProviderId?: string | null
  hubId: string
  resourceTypeId: number
  jobDate: Date,
  destinationLocationId: number
  pickupLocationIds: number[] | null
  destinationLocationTagIds: number[] | null
  pickupLocationTagIds: number[] | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LineItemSelect',
  props: {
    value: null,
    assignedToServiceProviderId: { default: null },
    hubId: null,
    resourceTypeId: null,
    jobDate: null,
    destinationLocationId: null,
    pickupLocationIds: null,
    destinationLocationTagIds: null,
    pickupLocationTagIds: null
  },
  emits: ["select-price-book-item"],
  setup(__props: any, { emit }: { emit: ({
  (
    e: 'select-price-book-item',
    value: {
      id: string | null
      name: string
      defaultRate: number | null
    }
  )
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: string, assignedToServiceProviderId: string | null, hubId: string, resourceTypeId: number, jobDate: Date, destinationLocationId: number, pickupLocationIds: number[] | null, destinationLocationTagIds: number[] | null, pickupLocationTagIds: number[] | null }





const selected = computed(() => props.value)

const { data: priceBookItems, isLoading } = getAllPriceBookItems()

const store = useStore()
const hasLineItemRateAccessMethod = computed<
  (serviceProviderId: string | null, hubId: string) => boolean
>(() => store.getters['permissions/hasLineItemRateAccess'])
const hasLineItemRateAccess = computed(() =>
  hasLineItemRateAccessMethod.value(
    props.assignedToServiceProviderId,
    props.hubId
  )
)

const { currentHub } = useHubs()

const formatter = useCurrencyFormatter(5)

const makeSortedPriceBookList = (items : PriceBookItem[]) => {
  /*
    Each criterion can have one of three results for each price book item:
    - true: the item's condition matches the job
    - false: the item's condition conflicts with the job
    - null: the item has no condition defined
    We then filter out mismatches, and sort by the number of matches
   */
  type CriteriaMatcher = (item: PriceBookItem) => boolean | null;
  const criteria : CriteriaMatcher[] = [
    (item) => item.associatedHubId === null ? null : item.associatedHubId === currentHub.value?.id,
    (item) => item.associatedResourceTypeIds.length === 0 ? null : item.associatedResourceTypeIds.includes(props.resourceTypeId),
    (item) => {
      if (item.validFrom === null && item.validUntil === null) {
        return null;
      }

      let earlyEnough = true;
      if (item.validUntil !== null) {
        const firstInvalidDate = new Date(item.validUntil.getTime());
        firstInvalidDate.setDate(item.validUntil.getDate() + 1);
        earlyEnough = props.jobDate < firstInvalidDate;
      }

      const lateEnough = item.validFrom === null ? true : item.validFrom <= props.jobDate;

      return lateEnough && earlyEnough;
    },
    (item) => {
      return item.pickupLocationIds.length > 0
        ? item.pickupLocationIds.some((id) => props.pickupLocationIds?.includes(id))
        : null
    },
    (item) => {
      return item.pickupLocationTagIds.length > 0
        ? item.pickupLocationTagIds.some((id) => props.pickupLocationTagIds?.includes(id))
        : null
    },
    (item) => {
      return item.destinationLocationIds.length > 0
        ? item.destinationLocationIds.some((id) => id === props.destinationLocationId)
        : null
    },
    (item) => {
      return item.destinationLocationTagIds.length > 0
        ? item.destinationLocationTagIds.some((id) => props.destinationLocationTagIds?.includes(id))
        : null
    },
  ]

  type MatchedItem = {
    priceBookItem: PriceBookItem,
    criteriaMatches: number,
    shouldBeVisible: boolean
  }
  const wrappedItems = items.map((item) => {
    const wrapper : MatchedItem = {
      priceBookItem: item,
      criteriaMatches: 0,
      shouldBeVisible: true
    };

    for (const criterion of criteria) {
      switch (criterion(item)) {
        case true: wrapper.criteriaMatches++; break;
        case false: wrapper.shouldBeVisible = false; break;
      }
    }

    return wrapper;
  });

  return orderBy(
    wrappedItems.filter((w) => w.shouldBeVisible === true),
    [
      (w) => w.criteriaMatches,
      (w) => w.priceBookItem.name,
    ],
    ['desc', 'asc']
  ).map((w) => w.priceBookItem)
}

const transformedPriceBookOptions = computed(() => {
  const options: {
    text: string
    value: { id: string; defaultRate: number | null; name: string }
    description: string
    actionText: string | null
  }[] = []

  if (priceBookItems.value) {
    makeSortedPriceBookList(priceBookItems.value)
    .forEach((item) => {
      options.push({
        text: item.name,
        description: formatDescription(item.description),
        actionText:
          hasLineItemRateAccess.value && item.defaultRate
            ? formatter.format(item.defaultRate)
            : null,
        value: {
          id: item.id,
          name: item.name,
          defaultRate: item.defaultRate,
        },
      })
    })
  }

  return options
})

function formatDescription(description: string | null) {
  // truncate descriptions that are too long to display in a dropdown
  if (description && description.length > 125) {
    return description.slice(0, 125) + '...'
  }
  return description ?? ''
}

function updateLineItem(
  value:
    | string
    | { id: string; name: string; defaultRate: number | null }
    | null
) {
  if (!value) return

  if (typeof value === 'string') {
    // we don't want to re-emit if the name hasn't changed
    if (selected.value === value) return
    emit('select-price-book-item', {
      id: null,
      defaultRate: null,
      name: value,
    })
  } else {
    emit('select-price-book-item', {
      id: value.id,
      defaultRate: value.defaultRate,
      name: value.name,
    })
  }
}

return { __sfc: true,props, emit, selected, priceBookItems, isLoading, store, hasLineItemRateAccessMethod, hasLineItemRateAccess, currentHub, formatter, makeSortedPriceBookList, transformedPriceBookOptions, formatDescription, updateLineItem, VsAutocomplete, VsWrapper, VsTextInput }
}

})